import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Badge,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  ListItemIcon,
  InputBase,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  FlightTakeoff as FlightIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store';
import { logout } from '../../store/slices/authSlice';
import { customsClient } from '../../api/client';

interface ApiNotification {
  id: string;
  type: string;
  title: string;
  message: string;
  link?: string;
  read: boolean;
  createdAt: string;
}

interface HeaderProps {
  onMenuClick: () => void;
}

export function Header({ onMenuClick }: HeaderProps) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifAnchor, setNotifAnchor] = useState<null | HTMLElement>(null);
  const [searchValue, setSearchValue] = useState('');
  const [notifications, setNotifications] = useState<ApiNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const loadNotifications = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await customsClient.get(`/cds/notifications?userId=${user.id}&limit=10`);
      setNotifications(res.data.data || []);
      setUnreadCount(res.data.unreadCount || 0);
    } catch {
      // Notifications are non-critical — fail silently
    }
  }, [user?.id]);

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000); // poll every 30s
    return () => clearInterval(interval);
  }, [loadNotifications]);

  const handleMarkAllRead = async () => {
    if (!user?.id) return;
    try {
      await customsClient.post(`/cds/notifications/read-all?userId=${user.id}`);
      setUnreadCount(0);
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch { /* non-critical */ }
  };

  const handleProfileOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileClose = () => {
    setAnchorEl(null);
  };

  const handleNotifOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotifAnchor(event.currentTarget);
  };

  const handleNotifClose = () => {
    setNotifAnchor(null);
  };

  const handleLogout = () => {
    handleProfileClose();
    dispatch(logout());
    navigate('/login');
  };

  const handleSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchValue.trim()) {
      navigate(`/tracking?awb=${encodeURIComponent(searchValue.trim())}`);
      setSearchValue('');
    }
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        bgcolor: 'white',
        borderBottom: '1px solid #e2e8f0',
        ml: { lg: '260px' },
        width: { lg: 'calc(100% - 260px)' },
        zIndex: (theme) => theme.zIndex.drawer - 1,
      }}
    >
      <Toolbar sx={{ gap: 1, px: { xs: 2, md: 3 } }}>
        {/* Mobile menu toggle */}
        <IconButton
          edge="start"
          onClick={onMenuClick}
          sx={{ display: { lg: 'none' }, color: '#64748b' }}
        >
          <MenuIcon />
        </IconButton>

        {/* Search bar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            bgcolor: '#f1f5f9',
            borderRadius: 2,
            px: 2,
            py: 0.5,
            flexGrow: { xs: 1, md: 0 },
            width: { md: 320 },
            border: '1px solid transparent',
            '&:focus-within': {
              border: '1px solid #0066CC',
              bgcolor: 'white',
            },
            transition: 'all 0.15s ease',
          }}
        >
          <SearchIcon sx={{ color: '#94a3b8', fontSize: 18, mr: 1 }} />
          <InputBase
            placeholder="Track AWB number..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={handleSearch}
            sx={{
              fontSize: '0.875rem',
              color: '#1e293b',
              '& input::placeholder': { color: '#94a3b8' },
              flexGrow: 1,
            }}
          />
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        {/* Quick track button hint */}
        <Typography
          variant="caption"
          sx={{
            display: { xs: 'none', md: 'block' },
            color: '#94a3b8',
            mr: 1,
          }}
        >
          Press Enter to track
        </Typography>

        {/* Notifications */}
        <Tooltip title="Notifications">
          <IconButton onClick={handleNotifOpen} sx={{ color: '#64748b' }}>
            <Badge badgeContent={unreadCount || null} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>
        </Tooltip>

        {/* User avatar */}
        <Tooltip title="Account">
          <IconButton onClick={handleProfileOpen} sx={{ p: 0.5 }}>
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: '#0066CC',
                fontSize: '0.875rem',
                fontWeight: 700,
              }}
            >
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </Avatar>
          </IconButton>
        </Tooltip>
      </Toolbar>

      {/* Profile menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          elevation: 4,
          sx: {
            mt: 1,
            minWidth: 200,
            borderRadius: 2,
            border: '1px solid #e2e8f0',
            '& .MuiMenuItem-root': {
              borderRadius: 1,
              mx: 0.5,
              fontSize: '0.875rem',
            },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="body2" fontWeight={600} color="text.primary">
            {user?.firstName} {user?.lastName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user?.email}
          </Typography>
        </Box>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={() => { handleProfileClose(); navigate('/profile'); }}>
          <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
          Profile
        </MenuItem>
        <MenuItem onClick={() => { handleProfileClose(); navigate('/settings'); }}>
          <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
          Settings
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
          <ListItemIcon><LogoutIcon fontSize="small" color="error" /></ListItemIcon>
          Sign Out
        </MenuItem>
      </Menu>

      {/* Notifications menu */}
      <Menu
        anchorEl={notifAnchor}
        open={Boolean(notifAnchor)}
        onClose={handleNotifClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          elevation: 4,
          sx: {
            mt: 1,
            width: 340,
            borderRadius: 2,
            border: '1px solid #e2e8f0',
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle2" fontWeight={600}>
            Notifications {unreadCount > 0 && <Typography component="span" variant="caption" sx={{ bgcolor: '#0066CC', color: 'white', borderRadius: 99, px: 0.8, py: 0.2, ml: 0.5, fontWeight: 700 }}>{unreadCount}</Typography>}
          </Typography>
          {unreadCount > 0 && (
            <Typography variant="caption" color="primary" sx={{ cursor: 'pointer' }} onClick={handleMarkAllRead}>
              Mark all read
            </Typography>
          )}
        </Box>
        <Divider />
        {notifications.length === 0 ? (
          <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">No notifications yet</Typography>
          </Box>
        ) : notifications.map((notif) => {
          const iconColor = notif.type === 'customs_cleared' || notif.type === 'review_approved' ? '#10b981'
            : notif.type === 'documents_required' || notif.type === 'compliance_flag' ? '#f59e0b'
            : notif.type === 'review_rejected' ? '#ef4444'
            : '#0066CC';
          const timeAgo = (() => {
            const diff = Date.now() - new Date(notif.createdAt).getTime();
            if (diff < 60000) return 'just now';
            if (diff < 3600000) return `${Math.round(diff / 60000)}m ago`;
            if (diff < 86400000) return `${Math.round(diff / 3600000)}h ago`;
            return `${Math.round(diff / 86400000)}d ago`;
          })();
          return (
            <MenuItem
              key={notif.id}
              onClick={() => {
                handleNotifClose();
                if (notif.link) navigate(notif.link);
              }}
              sx={{
                py: 1.5, px: 2,
                bgcolor: notif.read ? 'transparent' : '#EFF6FF',
                '&:hover': { bgcolor: '#f1f5f9' },
                alignItems: 'flex-start', gap: 1.5,
              }}
            >
              <Box sx={{ mt: 0.25 }}>
                <FlightIcon fontSize="small" sx={{ color: iconColor }} />
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2" fontWeight={notif.read ? 400 : 600}>{notif.title}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>{notif.message.slice(0, 60)}{notif.message.length > 60 ? '…' : ''}</Typography>
                <Typography variant="caption" color="text.disabled">{timeAgo}</Typography>
              </Box>
              {!notif.read && (
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#0066CC', mt: 0.5, flexShrink: 0 }} />
              )}
            </MenuItem>
          );
        })}
        <Divider />
        <Box sx={{ p: 1 }}>
          <MenuItem
            onClick={handleNotifClose}
            sx={{ justifyContent: 'center', borderRadius: 1, color: 'primary.main', fontSize: '0.875rem' }}
          >
            View all notifications
          </MenuItem>
        </Box>
      </Menu>
    </AppBar>
  );
}
